import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Copy, ExternalLink, ThumbsUp } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";

const BitchutePage = () => {
  const [copied, setCopied] = useState<string | null>(null);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(text);
    toast.success("Link copiado para a área de transferência!");
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="container mx-auto px-4 pt-24 pb-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent py-2">
            BitChute - Plataforma Alternativa de Vídeos
          </h1>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            BitChute é uma plataforma de compartilhamento de vídeos que valoriza a liberdade de expressão. Crie sua conta e explore conteúdos alternativos.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          <Card className="overflow-hidden border border-border">
            <CardHeader className="bg-muted/50">
              <CardTitle className="text-xl">Canal Killer45 no BitChute</CardTitle>
              <CardDescription>
                Acesse o canal para conteúdos exclusivos e diferenciados
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="flex flex-col gap-4">
                <p className="text-sm text-muted-foreground">
                  O canal Killer45 traz conteúdos variados que você não encontrará em plataformas convencionais.
                </p>
                <div className="flex items-center justify-between">
                  <a 
                    href="https://old.bitchute.com/channel/jGs5mLAXFlSu/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="flex items-center text-primary hover:underline"
                  >
                    Visitar Canal <ExternalLink size={16} className="ml-1" />
                  </a>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleCopy("https://old.bitchute.com/channel/jGs5mLAXFlSu/")}
                    className={copied === "https://old.bitchute.com/channel/jGs5mLAXFlSu/" ? "bg-primary/10" : ""}
                  >
                    <Copy size={16} className="mr-1" /> 
                    {copied === "https://old.bitchute.com/channel/jGs5mLAXFlSu/" ? "Copiado!" : "Copiar Link"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="overflow-hidden border border-border">
            <CardHeader className="bg-muted/50">
              <CardTitle className="text-xl">Crie sua Conta no BitChute</CardTitle>
              <CardDescription>
                Use o link de referência para se registrar na plataforma
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="flex flex-col gap-4">
                <p className="text-sm text-muted-foreground">
                  Ao se registrar pelo link de referência, você apoia o canal Killer45 e desfruta de uma plataforma com menos censura.
                </p>
                <div className="flex items-center justify-between">
                  <a 
                    href="https://www.bitchute.com/accounts/referral/killer45/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="flex items-center text-primary hover:underline"
                  >
                    Criar Conta <ExternalLink size={16} className="ml-1" />
                  </a>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleCopy("https://www.bitchute.com/accounts/referral/killer45/")}
                    className={copied === "https://www.bitchute.com/accounts/referral/killer45/" ? "bg-primary/10" : ""}
                  >
                    <Copy size={16} className="mr-1" /> 
                    {copied === "https://www.bitchute.com/accounts/referral/killer45/" ? "Copiado!" : "Copiar Link"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="bg-muted p-6 rounded-lg mb-12">
          <h2 className="text-2xl font-semibold mb-4">Por que escolher o BitChute?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex flex-col items-center text-center p-4">
              <ThumbsUp className="h-12 w-12 text-primary mb-2" />
              <h3 className="text-lg font-medium mb-2">Liberdade de Expressão</h3>
              <p className="text-sm text-muted-foreground">Menos censura e mais liberdade para criadores e espectadores.</p>
            </div>
            <div className="flex flex-col items-center text-center p-4">
              <ThumbsUp className="h-12 w-12 text-primary mb-2" />
              <h3 className="text-lg font-medium mb-2">Conteúdo Alternativo</h3>
              <p className="text-sm text-muted-foreground">Acesso a vídeos e informações que podem ser censurados em outras plataformas.</p>
            </div>
            <div className="flex flex-col items-center text-center p-4">
              <ThumbsUp className="h-12 w-12 text-primary mb-2" />
              <h3 className="text-lg font-medium mb-2">Apoie Criadores Independentes</h3>
              <p className="text-sm text-muted-foreground">Sua participação ajuda a manter criadores independentes produzindo conteúdo.</p>
            </div>
          </div>
        </div>

        <div className="text-center">
          <Button 
            asChild 
            size="lg"
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            <a 
              href="https://www.bitchute.com/accounts/referral/killer45/" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              Criar Minha Conta Agora
            </a>
          </Button>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BitchutePage;